/**
 * RegistrationProgressTracker - Visual progress with donut chart, thermometer, and toggles
 * Shows event registration progress with interactive visualizations
 */
import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Users, Target, TrendingUp, Eye, EyeOff } from 'lucide-react';

// Tier color palette
const TIER_COLORS = [
  '#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6',
  '#ec4899', '#06b6d4', '#f97316', '#14b8a6', '#a855f7'
];

// ===================== DONUT CHART =====================
const DonutChart = ({ segments, centerLabel, centerValue, showAmounts, showNames, size = 200 }) => {
  const radius = 70;
  const strokeWidth = 28;
  const cx = 100;
  const cy = 100;
  const circumference = 2 * Math.PI * radius;
  const total = segments.reduce((s, seg) => s + seg.value, 0);

  let accumulated = 0;
  const arcs = segments.map((seg, i) => {
    const pct = total > 0 ? seg.value / total : 0;
    const dashLength = pct * circumference;
    const dashOffset = circumference - (accumulated / (total || 1)) * circumference;
    accumulated += seg.value;
    return { ...seg, pct, dashLength, dashOffset, index: i };
  });

  const [hoveredIdx, setHoveredIdx] = useState(null);

  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 200 200" width={size} height={size} className="drop-shadow-sm">
        {/* Background ring */}
        <circle cx={cx} cy={cy} r={radius} fill="none" stroke="#f1f5f9" strokeWidth={strokeWidth} />
        {/* Segments */}
        {arcs.map((arc) => (
          <circle
            key={arc.index}
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke={arc.color}
            strokeWidth={hoveredIdx === arc.index ? strokeWidth + 6 : strokeWidth}
            strokeDasharray={`${arc.dashLength} ${circumference - arc.dashLength}`}
            strokeDashoffset={arc.dashOffset}
            strokeLinecap="butt"
            transform={`rotate(-90 ${cx} ${cy})`}
            style={{
              transition: 'stroke-width 0.2s ease, opacity 0.2s ease',
              opacity: hoveredIdx !== null && hoveredIdx !== arc.index ? 0.5 : 1,
              cursor: 'pointer'
            }}
            onMouseEnter={() => setHoveredIdx(arc.index)}
            onMouseLeave={() => setHoveredIdx(null)}
          />
        ))}
        {/* Center text */}
        <text x={cx} y={cy - 8} textAnchor="middle" className="text-2xl font-bold" fill="#1e293b" fontSize="24">
          {centerValue}
        </text>
        <text x={cx} y={cy + 14} textAnchor="middle" className="text-xs" fill="#94a3b8" fontSize="11">
          {centerLabel}
        </text>
      </svg>

      {/* Legend */}
      {(showNames || showAmounts) && (
        <div className="mt-3 w-full space-y-1.5">
          {arcs.map((arc) => (
            <div
              key={arc.index}
              className={cn(
                "flex items-center justify-between px-2 py-1 rounded transition-colors",
                hoveredIdx === arc.index ? "bg-slate-100" : ""
              )}
              onMouseEnter={() => setHoveredIdx(arc.index)}
              onMouseLeave={() => setHoveredIdx(null)}
            >
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: arc.color }} />
                {showNames && <span className="text-sm text-slate-700 truncate max-w-[120px]">{arc.name}</span>}
              </div>
              {showAmounts && (
                <span className="text-sm font-medium tabular-nums text-slate-600">
                  {arc.displayValue}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ===================== THERMOMETER =====================
const Thermometer = ({ value, max, label, showAmount, color = '#6366f1' }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const displayPct = Math.round(pct);

  // Color based on fill level
  const fillColor = pct > 90 ? '#ef4444' : pct > 70 ? '#f59e0b' : color;

  return (
    <div className="flex flex-col items-center">
      {/* Value display */}
      <div className="text-center mb-2">
        <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
        <div className="text-lg font-bold text-slate-800">
          {showAmount ? value : `${displayPct}%`}
          {showAmount && <span className="text-sm font-normal text-slate-400"> / {max}</span>}
        </div>
      </div>

      {/* Thermometer body */}
      <div className="relative w-10 h-40 flex flex-col items-center">
        {/* Tube */}
        <div className="relative w-8 h-32 bg-slate-100 rounded-full overflow-hidden border border-slate-200">
          {/* Fill */}
          <div
            className="absolute bottom-0 left-0 right-0 rounded-full transition-all duration-700 ease-out"
            style={{
              height: `${pct}%`,
              background: `linear-gradient(to top, ${fillColor}, ${fillColor}dd)`,
            }}
          />
          {/* Graduation marks */}
          {[25, 50, 75].map(mark => (
            <div
              key={mark}
              className="absolute left-0 right-0 border-t border-slate-200/60"
              style={{ bottom: `${mark}%` }}
            />
          ))}
        </div>
        {/* Bulb */}
        <div
          className="w-12 h-12 rounded-full -mt-2 border-2 border-slate-200 flex items-center justify-center shadow-inner"
          style={{ backgroundColor: fillColor + '20', borderColor: fillColor + '40' }}
        >
          <div
            className="w-8 h-8 rounded-full"
            style={{ backgroundColor: fillColor }}
          />
        </div>
      </div>

      {/* Percentage below */}
      <div className="mt-1 text-xs font-medium" style={{ color: fillColor }}>
        {displayPct}%
      </div>
    </div>
  );
};

// ===================== MAIN COMPONENT =====================
const RegistrationProgressTracker = ({ event, delegates = [], tiers = [] }) => {
  const [showNames, setShowNames] = useState(true);
  const [showAmounts, setShowAmounts] = useState(true);
  const [displayMode, setDisplayMode] = useState('amounts'); // 'amounts' or 'percentages'

  const stats = useMemo(() => {
    const totalCapacity = tiers.reduce((acc, t) => acc + (t.capacity || 0), 0);
    const totalRegistered = delegates.length;
    const checkedIn = delegates.filter(d => d.status === 'checked_in').length;
    const confirmed = delegates.filter(d => d.status === 'confirmed').length;
    const pending = delegates.filter(d => d.status === 'pending').length;

    // Count by tier
    const tierCounts = {};
    delegates.forEach(d => {
      const tid = d.tier_id || 'unassigned';
      tierCounts[tid] = (tierCounts[tid] || 0) + 1;
    });

    // Build donut segments
    const tierSegments = tiers.map((t, i) => {
      const count = tierCounts[t.id] || 0;
      const pctOfTotal = totalRegistered > 0 ? ((count / totalRegistered) * 100).toFixed(1) : '0.0';
      return {
        name: t.name,
        value: count,
        color: t.color || TIER_COLORS[i % TIER_COLORS.length],
        displayValue: displayMode === 'amounts' ? `${count}` : `${pctOfTotal}%`,
      };
    });

    // Add unassigned if any
    if (tierCounts['unassigned']) {
      tierSegments.push({
        name: 'Unassigned',
        value: tierCounts['unassigned'],
        color: '#94a3b8',
        displayValue: displayMode === 'amounts'
          ? `${tierCounts['unassigned']}`
          : `${totalRegistered > 0 ? ((tierCounts['unassigned'] / totalRegistered) * 100).toFixed(1) : '0.0'}%`,
      });
    }

    // Status segments - dynamically built from actual data
    const statusCounts = {};
    delegates.forEach(d => {
      const s = d.status || 'unknown';
      statusCounts[s] = (statusCounts[s] || 0) + 1;
    });

    const STATUS_COLORS = {
      registered: '#6366f1',
      checked_in: '#10b981',
      confirmed: '#22c55e',
      pending: '#f59e0b',
      pending_payment: '#f97316',
      interested: '#8b5cf6',
      paid: '#06b6d4',
      cancelled: '#ef4444',
      waitlisted: '#94a3b8',
      unknown: '#cbd5e1',
    };

    const STATUS_LABELS = {
      registered: 'Registered',
      checked_in: 'Checked In',
      confirmed: 'Confirmed',
      pending: 'Pending',
      pending_payment: 'Pending Payment',
      interested: 'Interested',
      paid: 'Paid',
      cancelled: 'Cancelled',
      waitlisted: 'Waitlisted',
      unknown: 'Unknown',
    };

    const statusSegments = Object.entries(statusCounts)
      .filter(([, count]) => count > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([status, count]) => ({
        name: STATUS_LABELS[status] || status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' '),
        value: count,
        color: STATUS_COLORS[status] || TIER_COLORS[Object.keys(statusCounts).indexOf(status) % TIER_COLORS.length],
        displayValue: displayMode === 'amounts'
          ? `${count}`
          : `${totalRegistered > 0 ? ((count / totalRegistered) * 100).toFixed(1) : '0.0'}%`,
      }));

    return { totalCapacity, totalRegistered, checkedIn, confirmed, pending, tierSegments, statusSegments };
  }, [delegates, tiers, displayMode]);

  return (
    <Card data-testid="registration-progress-tracker">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <TrendingUp className="h-5 w-5 text-indigo-500" />
            Registration Progress
          </CardTitle>
          <div className="flex items-center gap-4 flex-wrap">
            {/* Display mode toggle */}
            <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
              <button
                data-testid="toggle-amounts"
                onClick={() => setDisplayMode('amounts')}
                className={cn(
                  "px-3 py-1 text-xs font-medium rounded-md transition-all",
                  displayMode === 'amounts'
                    ? "bg-white text-slate-800 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                )}
              >
                Amounts
              </button>
              <button
                data-testid="toggle-percentages"
                onClick={() => setDisplayMode('percentages')}
                className={cn(
                  "px-3 py-1 text-xs font-medium rounded-md transition-all",
                  displayMode === 'percentages'
                    ? "bg-white text-slate-800 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                )}
              >
                Percentages
              </button>
            </div>
            {/* Name toggle */}
            <div className="flex items-center gap-1.5">
              <Switch
                id="show-names"
                data-testid="toggle-names"
                checked={showNames}
                onCheckedChange={setShowNames}
                className="scale-75"
              />
              <Label htmlFor="show-names" className="text-xs text-slate-500 cursor-pointer">
                Names
              </Label>
            </div>
            {/* Amount toggle */}
            <div className="flex items-center gap-1.5">
              <Switch
                id="show-amounts"
                data-testid="toggle-amounts-legend"
                checked={showAmounts}
                onCheckedChange={setShowAmounts}
                className="scale-75"
              />
              <Label htmlFor="show-amounts" className="text-xs text-slate-500 cursor-pointer">
                Values
              </Label>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Donut: Tier Breakdown */}
          <div className="flex flex-col items-center">
            <h3 className="text-sm font-medium text-slate-600 mb-3">By Tier</h3>
            {stats.tierSegments.length > 0 ? (
              <DonutChart
                segments={stats.tierSegments}
                centerLabel="Registered"
                centerValue={displayMode === 'amounts' ? stats.totalRegistered : `${stats.totalCapacity > 0 ? Math.round((stats.totalRegistered / stats.totalCapacity) * 100) : 0}%`}
                showAmounts={showAmounts}
                showNames={showNames}
                size={180}
              />
            ) : (
              <div className="w-full max-w-[180px] h-[180px] rounded-full bg-slate-100 flex items-center justify-center">
                <span className="text-sm text-slate-400">No tiers</span>
              </div>
            )}
          </div>

          {/* Donut: Status Breakdown */}
          <div className="flex flex-col items-center">
            <h3 className="text-sm font-medium text-slate-600 mb-3">By Status</h3>
            {stats.statusSegments.length > 0 ? (
              <DonutChart
                segments={stats.statusSegments}
                centerLabel="Total"
                centerValue={displayMode === 'amounts' ? stats.totalRegistered : '100%'}
                showAmounts={showAmounts}
                showNames={showNames}
                size={180}
              />
            ) : (
              <div className="w-full max-w-[180px] h-[180px] rounded-full bg-slate-100 flex items-center justify-center">
                <span className="text-sm text-slate-400">No data</span>
              </div>
            )}
          </div>

          {/* Thermometer: Capacity */}
          <div className="flex flex-col items-center">
            <h3 className="text-sm font-medium text-slate-600 mb-3">Capacity</h3>
            <div className="flex gap-6 justify-center">
              <Thermometer
                value={stats.totalRegistered}
                max={stats.totalCapacity || stats.totalRegistered || 1}
                label="Registered"
                showAmount={displayMode === 'amounts'}
                color="#6366f1"
              />
              <Thermometer
                value={stats.checkedIn}
                max={stats.totalRegistered || 1}
                label="Checked In"
                showAmount={displayMode === 'amounts'}
                color="#10b981"
              />
            </div>
          </div>
        </div>

        {/* Summary row */}
        <div className="mt-6 pt-4 border-t flex flex-wrap gap-3 justify-center">
          <Badge variant="outline" className="text-xs gap-1.5 px-3 py-1">
            <Users className="h-3 w-3" />
            {stats.totalRegistered} Registered
          </Badge>
          <Badge variant="outline" className="text-xs gap-1.5 px-3 py-1 border-green-200 text-green-700">
            <Target className="h-3 w-3" />
            {stats.checkedIn} Checked In
          </Badge>
          <Badge variant="outline" className="text-xs gap-1.5 px-3 py-1 border-indigo-200 text-indigo-700">
            {stats.confirmed} Confirmed
          </Badge>
          {stats.totalCapacity > 0 && (
            <Badge variant="outline" className="text-xs gap-1.5 px-3 py-1 border-amber-200 text-amber-700">
              {stats.totalCapacity - stats.totalRegistered} Spots Left
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default RegistrationProgressTracker;
